import { ZapIcon, InhalerIcon, PillIcon, HeartIcon, AlertIcon, DumbbellIcon, UtensilsIcon, ActivityIcon, IconProps } from '../components/icons';
import { Tint } from './mock';
import React from 'react';

/* ---------- tracker types (tracking bottom modal picker) ---------- */

export type Tracker = {
  id: string;
  title: string;
  hint: string;
  icon: (p: IconProps) => React.ReactElement;
  tint: Tint;
};
export const TRACKERS: Tracker[] = [{
  id: 'seizures',
  title: 'Seizures',
  hint: 'Log an episode',
  icon: ZapIcon,
  tint: 'lilac'
}, {
  id: 'asthma-medication',
  title: 'Asthma Medication',
  hint: 'Inhaler & preventer',
  icon: InhalerIcon,
  tint: 'sky'
}, {
  id: 'epilepsy-medication',
  title: 'Epilepsy Medication',
  hint: 'Daily medication',
  icon: PillIcon,
  tint: 'coral'
}, {
  id: 'mood-gratitude',
  title: 'Mood & Gratitude',
  hint: 'How are you feeling?',
  icon: HeartIcon,
  tint: 'butter'
}, {
  id: 'asthma-attack',
  title: 'Asthma Attack',
  hint: 'Record what happened',
  icon: AlertIcon,
  tint: 'coral'
}, {
  id: 'workout',
  title: 'Workout',
  hint: 'Strength & sessions',
  icon: DumbbellIcon,
  tint: 'sage'
}, {
  id: 'eating-drinking',
  title: 'Eating & Drinking',
  hint: 'Meals and hydration',
  icon: UtensilsIcon,
  tint: 'teal'
}, {
  id: 'exercise',
  title: 'Exercise',
  hint: 'Activity & movement',
  icon: ActivityIcon,
  tint: 'sage'
}];

/* ---------- tracking history (Phase C) ---------- */
/* Two weeks of logged entries for the asthma persona: a steady preventer
   routine, the reliever only when needed (one mild attack on Friday 29 May),
   mood check-ins most evenings, and movement a few times a week. All mock —
   in production this is the local log store the conversational layer reads. */

export type TrackingEntry = {
  trackerId: string;
  detail: string;
  time: string;
};
export type TrackingDay = {
  label: string;
  entries: TrackingEntry[];
};
export const TRACKING_HISTORY: TrackingDay[] = [{
  label: 'Today',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:05 am'
  }, {
    trackerId: 'eating-drinking',
    detail: 'Porridge and a big glass of water',
    time: '8:20 am'
  }]
}, {
  label: 'Yesterday',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:10 am'
  }, {
    trackerId: 'workout',
    detail: '25 min strength session — felt strong',
    time: '6:30 pm'
  }, {
    trackerId: 'mood-gratitude',
    detail: 'Good day · grateful for the long evening light',
    time: '9:45 pm'
  }, {
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '10:00 pm'
  }]
}, {
  label: 'Sunday 31 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:30 am'
  }, {
    trackerId: 'exercise',
    detail: 'Long walk by the canal · 50 min',
    time: '2:15 pm'
  }, {
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '10:10 pm'
  }]
}, {
  label: 'Saturday 30 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:45 am'
  }, {
    trackerId: 'asthma-medication',
    detail: '2 puffs of your reliever · pollen felt high',
    time: '1:30 pm'
  }, {
    trackerId: 'eating-drinking',
    detail: 'Picnic lunch · plenty of water',
    time: '1:45 pm'
  }, {
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '9:50 pm'
  }]
}, {
  label: 'Friday 29 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:00 am'
  }, {
    trackerId: 'asthma-attack',
    detail: 'Mild · at the park · recovered in 10 min',
    time: '4:20 pm'
  }, {
    trackerId: 'asthma-medication',
    detail: '4 puffs of your reliever · during the attack',
    time: '4:22 pm'
  }, {
    trackerId: 'mood-gratitude',
    detail: 'A bit shaken, but proud of staying calm',
    time: '8:30 pm'
  }]
}, {
  label: 'Thursday 28 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:15 am'
  }, {
    trackerId: 'workout',
    detail: '20 min strength session',
    time: '6:00 pm'
  }, {
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '10:05 pm'
  }]
}, {
  label: 'Wednesday 27 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:05 am'
  }, {
    trackerId: 'mood-gratitude',
    detail: 'Tired but calm · grateful for an early night',
    time: '9:15 pm'
  }]
}, {
  label: 'Tuesday 26 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:20 am'
  }, {
    trackerId: 'eating-drinking',
    detail: 'Cooked a proper dinner · veg curry',
    time: '7:30 pm'
  }, {
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '10:15 pm'
  }]
}, {
  label: 'Monday 25 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:00 am'
  }, {
    trackerId: 'exercise',
    detail: 'Cycled to work and back',
    time: '6:45 pm'
  }]
}, {
  label: 'Sunday 24 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '9:00 am'
  }, {
    trackerId: 'mood-gratitude',
    detail: 'Slow, easy Sunday · felt rested',
    time: '8:50 pm'
  }]
}, {
  label: 'Saturday 23 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '9:20 am'
  }]
}, {
  label: 'Friday 22 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:10 am'
  }, {
    trackerId: 'workout',
    detail: '30 min session · new personal best',
    time: '5:45 pm'
  }]
}, {
  label: 'Thursday 21 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:05 am'
  }]
}, {
  label: 'Wednesday 20 May',
  entries: [{
    trackerId: 'asthma-medication',
    detail: '2 puffs of your preventer',
    time: '8:00 am'
  }, {
    trackerId: 'eating-drinking',
    detail: 'Meal-prepped lunches for the week',
    time: '7:00 pm'
  }]
}];

/* ---------- the inhaler chart (last 7 days) ---------- */
/* Puff counts per day — preventer is the steady routine (2 puffs morning +
   evening), the reliever only appears when it was needed. Friday is the
   attack day; today's evening preventer dose hasn't happened yet. */

export type InhalerDay = {
  day: string;
  preventer: number;
  reliever: number;
};
export const INHALER_WEEK: InhalerDay[] = [{
  day: 'Wed',
  preventer: 4,
  reliever: 0
}, {
  day: 'Thu',
  preventer: 4,
  reliever: 0
}, {
  day: 'Fri',
  preventer: 4,
  reliever: 4
}, {
  day: 'Sat',
  preventer: 4,
  reliever: 2
}, {
  day: 'Sun',
  preventer: 4,
  reliever: 0
}, {
  day: 'Mon',
  preventer: 4,
  reliever: 0
}, {
  day: 'Tue',
  preventer: 2,
  reliever: 0
}];

/* ---------- history headline stats ---------- */

export const HISTORY_STATS = [{
  value: '35',
  label: 'things logged'
}, {
  value: '13',
  label: 'active days'
}, {
  value: '6',
  label: 'reliever puffs'
}];